import { type DrawableMixin } from "../objects/drawable_mixin"
import PathObject from "../objects/path"
import Form, { type CurveOptions } from "./base"
import Calc from "@/common/util/calculator"
import pathRounding from "@/signomatic/util/path_rounding"

export default abstract class Direction extends Form {
  protected readonly flangedOffset = this.flangedHeight + this.drawables.cutlineWidth * 2

  protected path(width: number, height: number, rounding?: number) {
    const [side, rest] = this.snappableCommands(width, height, false)
    rest.shift()
    const cmds = side.concat(rest)

    cmds.push(this.pathClose())
    let path = cmds.join(" ")

    if (rounding) {
      path = pathRounding(path, rounding).join("")
    }

    return new PathObject(path, this.defaultParams)
  }

  private flanged(width: number, height: number) {
    const [side, rest] = this.snappableCommands(width, height, true)
    rest.shift()
    const cmds = side.concat(rest)

    cmds.push(this.pathClose())
    const path = cmds.join(" ")

    return new PathObject(path, this.defaultParams)
  }

  protected cutlinePath(
    width: number,
    height: number,
    rounding?: number,
    curves?: CurveOptions,
    forClip?: boolean
  ): DrawableMixin {
    if (this.state.fixmethod === "pole_flanged" && !forClip) {
      return this.flanged(width, height)
    }

    return this.path(width, height, rounding)
  }

  protected flangedPath(width: number, height: number) {
    if (this.state.fixmethod !== "pole_flanged") {
      return null
    }

    return this.flanged(width, height)
  }

  protected snappablePaths(width: number, height: number): [DrawableMixin, DrawableMixin] {
    const [side, rest] = this.snappableCommands(width, height, false)

    return [new PathObject(side.join(""), this.defaultParams), new PathObject(rest.join(""), this.defaultParams)]
  }

  protected abstract snappableCommands(width: number, height: number, flanged: boolean): [string[], string[]]

  protected get baseRounding() {
    return this.signType == "aluminium_sign" ||
      this.signType === "wood_sign" ||
      (this.signType === "double_sided_sign" &&
        (this.state.material == "regular" || this.state.material == "aluminium"))
      ? this.printCutlineRounding(Calc.mmToPx(0.6))
      : 0
  }

  protected get cutlineRounding() {
    return (this.signType == "aluminium_sign" && this.state.fixmethod !== "pole_flanged") ||
      this.signType === "wood_sign" ||
      (this.signType === "double_sided_sign" &&
        (this.state.material == "regular" || this.state.material == "aluminium"))
      ? this.printCutlineRounding(Calc.mmToPx(0.6))
      : 0
  }

  protected flangedHoles(width: number, height: number) {
    const holes: DrawableMixin[] = []

    const flatWidth = width - height / 2
    const directionOffset = this.state.shape == "direction_left" ? height / 4 : -(height / 4)

    const minHoles = 1
    const holesHorizontal = Math.max(
      Math.floor(((flatWidth - this.flangedOffset * 2) / 2 - this.flangedHoleWidth / 2) / this.flangedHoleOffset),
      minHoles
    )

    holes.push(this.flangedHole(directionOffset, -(height / 2) - this.flangedHeight / 2, "horizontal"))
    holes.push(this.flangedHole(directionOffset, height / 2 + this.flangedHeight / 2, "horizontal"))

    for (let index = 0; index < holesHorizontal; index++) {
      const offset = index + 1

      holes.push(
        this.flangedHole(
          directionOffset - offset * this.flangedHoleOffset,
          -height / 2 - this.flangedHeight / 2,
          "horizontal"
        )
      )
      holes.push(
        this.flangedHole(
          directionOffset + offset * this.flangedHoleOffset,
          -height / 2 - this.flangedHeight / 2,
          "horizontal"
        )
      )

      holes.push(
        this.flangedHole(
          directionOffset - offset * this.flangedHoleOffset,
          height / 2 + this.flangedHeight / 2,
          "horizontal"
        )
      )
      holes.push(
        this.flangedHole(
          directionOffset + offset * this.flangedHoleOffset,
          height / 2 + this.flangedHeight / 2,
          "horizontal"
        )
      )
    }

    return holes
  }
}
